import { forwardRef, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank, Close } from "@mui/icons-material";
import { t } from "i18next";
import { useSelector } from "../../../../../../../app/helpers";
import { RootState } from "../../../../../../../app/rootReducer";
import { WeekdayNames } from "../../../../../Domain/Types/FloorPlan/WeekdayNames.type";
import { PermissionUnitEntries } from "../PermissionSelect";

type WeekdayPermissionDialogProps = {
  dialogOpen: boolean;
  option: PermissionUnitEntries | null;
  initialPermWeekdays: WeekdayNames[] | undefined;
  handleActiveWkdayChange: (option: any, value: WeekdayNames[]) => void;
  setIsWkdayPermDialogOpen: (o: boolean) => void;
};
export const WeekdayPermissionDialog: React.FC<WeekdayPermissionDialogProps> = forwardRef<
  HTMLDivElement,
  WeekdayPermissionDialogProps
>((props, ref) => {
  const {
    dialogOpen,
    option,
    initialPermWeekdays,
    handleActiveWkdayChange,
    setIsWkdayPermDialogOpen
  } = props;
  const { bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed } = useSelector(
    (state: RootState) => state.login.userInformation.company.meta
  );

  const [newVal, setNewVal] = useState<WeekdayNames[]>(initialPermWeekdays ?? []);

  const handleWeekdayToggle = (
    event: React.MouseEvent<HTMLElement>,
    newToggles: WeekdayNames[]
  ) => {
    setNewVal(newToggles);
  };

  const handleClose = () => setIsWkdayPermDialogOpen(false);

  const handleSave = () => {
    handleActiveWkdayChange(option, newVal);
    handleClose();
  };

  useEffect(() => {
    if (initialPermWeekdays) setNewVal(initialPermWeekdays);
  }, [initialPermWeekdays]);

  return (
    <Dialog open={dialogOpen} ref={ref}>
      <DialogTitle>
        <Grid2 container data-testid="restrict-perm-per-weekday">
          <Grid2 size={{ xs: 1 }} />
          <Grid2 sx={{ textAlign: "center", alignSelf: "center" }} size={{ xs: 10 }}>
            <Typography variant="h6">{t("Restrict Permissions Per Weekday")}</Typography>
          </Grid2>

          <Grid2 sx={{ textAlign: "right" }} size={{ xs: 1 }}>
            <IconButton data-testid="btn-close" onClick={handleClose} size="large">
              <Close />
            </IconButton>
          </Grid2>
        </Grid2>
      </DialogTitle>

      <DialogContent>
        <ToggleButtonGroup
          value={newVal}
          onChange={handleWeekdayToggle}
          data-testid="weekday-perm-toggle-btn-group"
          sx={{ display: "flex", flexWrap: "wrap" }}
        >
          <WeekdayPermToggle
            weekday={WeekdayNames.MONDAY}
            initialValue={newVal.includes(WeekdayNames.MONDAY)}
          />
          <WeekdayPermToggle
            weekday={WeekdayNames.TUESDAY}
            initialValue={newVal.includes(WeekdayNames.TUESDAY)}
          />
          <WeekdayPermToggle
            weekday={WeekdayNames.WEDNESDAY}
            initialValue={newVal.includes(WeekdayNames.WEDNESDAY)}
          />
          <WeekdayPermToggle
            weekday={WeekdayNames.THURSDAY}
            initialValue={newVal.includes(WeekdayNames.THURSDAY)}
          />
          <WeekdayPermToggle
            weekday={WeekdayNames.FRIDAY}
            initialValue={newVal.includes(WeekdayNames.FRIDAY)}
          />
          {bookingsOnSaturdaysAllowed && (
            <WeekdayPermToggle
              weekday={WeekdayNames.SATURDAY}
              initialValue={newVal.includes(WeekdayNames.SATURDAY)}
            />
          )}
          {bookingsOnSundaysAllowed && (
            <WeekdayPermToggle
              weekday={WeekdayNames.SUNDAY}
              initialValue={newVal.includes(WeekdayNames.SUNDAY)}
            />
          )}
        </ToggleButtonGroup>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", padding: "16px 24px" }}>
        <Button data-testid={"weekday-perm-save-btn"} onClick={handleSave} sx={{ width: "100%" }}>
          {t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

type WeekdayPermToggleProps = {
  weekday: WeekdayNames;
  initialValue: boolean;
};

export const WeekdayPermToggle: React.FC<WeekdayPermToggleProps> = ({ weekday, initialValue }) => {
  return (
    <ToggleButton value={weekday} data-testid={`${weekday}-toggle-btn`}>
      {t(weekday)}
      {initialValue ? (
        <CheckBox sx={{ ml: 1 }} color="primary" />
      ) : (
        <CheckBoxOutlineBlank sx={{ ml: 1 }} color="primary" />
      )}
    </ToggleButton>
  );
};
